// eslint-disable-next-line import/no-extraneous-dependencies
import { Dispatch } from 'redux';

import * as actionTypes from '../../constants/actionTypes';
import SentryUtils from '../../lib/sentry';
import { ThunkDispatch } from '../../meta/types/actions';
import { Payer, UserBillingInfoDto } from '../../meta/types/billing/billingInfo';
import { mapOrder, Order } from '../../meta/types/billing/order';
import {
  CountryRegulation,
  VatCalculateResponse,
  VatVerificationResponse,
} from '../../meta/types/billing/vat';
import { HttpStatus } from '../../meta/types/http';
import { StoreState } from '../../meta/types/store';
import BillingInfoResource from '../../services/Dashboard/BillingInfoResource';
import CountryRegulationsResource from '../../services/Dashboard/CountryRegulationsResource';
import OrderResource from '../../services/Dashboard/OrderResource';
import SalesPackageResource from '../../services/Dashboard/SalesPackageResource';
import { SalesPackage, SalesPackageSelector } from '../../meta/types/billing/salespackage';

export interface IConfirmPurchaseOrder {
  type: actionTypes.CONFIRM_PURCHASE_ORDER;
  order: Order;
}

export const confirmPurchaseOrder = (order: Order): IConfirmPurchaseOrder => ({
  type: actionTypes.CONFIRM_PURCHASE_ORDER,
  order,
});

export interface IUpdatePurchaseOrder {
  type: actionTypes.UPDATE_PURCHASE_ORDER;
  order: Order;
}

export const updatePurchaseOrder = (order: Order): IUpdatePurchaseOrder => ({
  type: actionTypes.UPDATE_PURCHASE_ORDER,
  order,
});

export interface ICloseManagementOrderPopup {
  type: actionTypes.CLOSE_MANAGEMENT_ORDER_POPUP;
}

export const closeManagementOrderPopup = (): ICloseManagementOrderPopup => ({
  type: actionTypes.CLOSE_MANAGEMENT_ORDER_POPUP,
});

export interface ISetManagementIssuedOrderFetching {
  type: actionTypes.SET_MANAGEMENT_ISSUED_ORDER_FETCHING;
  fetching: boolean;
}

export const setManagementIssuedOrderFetching = (
  fetching: boolean,
): ISetManagementIssuedOrderFetching => ({
  type: actionTypes.SET_MANAGEMENT_ISSUED_ORDER_FETCHING,
  fetching,
});

export interface ISetManagementIssuedOrder {
  type: actionTypes.SET_MANAGEMENT_ISSUED_ORDER;
  order?: Order;
}

export const setManagementIssuedOrder = (order: Order | undefined): ISetManagementIssuedOrder => ({
  type: actionTypes.SET_MANAGEMENT_ISSUED_ORDER,
  order,
});

export const getManagementIssuedOrder = (userId: string) => {
  return async (dispatch: Dispatch, getState: () => StoreState): Promise<Order | undefined> => {
    const billingState = getState().billing;
    if (billingState.managementOfferFetched) {
      return billingState.managementIssuedOrder;
    }
    dispatch(setManagementIssuedOrderFetching(true));
    try {
      const resp = await OrderResource.getManagementOffer({ userId });
      const order = mapOrder(resp);
      dispatch(setManagementIssuedOrder(order));
      return order;
    } catch (apiError) {
      dispatch(setManagementIssuedOrderFetching(false));
      if (apiError.status !== HttpStatus.NOT_FOUND) {
        const error = new Error(apiError.message);
        SentryUtils.captureAPIError(apiError, error.message);
        throw error;
      }
      return undefined;
    }
  };
};

export interface IUpdateUserBillingInfo {
  type: actionTypes.UPDATE_USER_BILLING_INFO;
  userBillingInfo: UserBillingInfoDto;
}

export const updateUserBillingInfo = (
  userBillingInfo: UserBillingInfoDto,
): IUpdateUserBillingInfo => ({
  type: actionTypes.UPDATE_USER_BILLING_INFO,
  userBillingInfo,
});

export interface IRemoveUserBillingInfo {
  type: actionTypes.REMOVE_USER_BILLING_INFO;
  id: number;
}

export const removeUserBillingInfo = (id: number): IRemoveUserBillingInfo => ({
  type: actionTypes.REMOVE_USER_BILLING_INFO,
  id,
});

export interface IAddUserBillingInfo {
  type: actionTypes.ADD_USER_BILLING_INFO;
  userBillingInfo: UserBillingInfoDto;
}

export const addUserBillingInfo = (userBillingInfo: UserBillingInfoDto): IAddUserBillingInfo => ({
  type: actionTypes.ADD_USER_BILLING_INFO,
  userBillingInfo,
});

export interface ISetUserBillingInfo {
  type: actionTypes.SET_USER_BILLING_INFO;
  userBillingInfo: UserBillingInfoDto[];
}

const setUserBillingInfo = (userBillingInfo: UserBillingInfoDto[]): ISetUserBillingInfo => ({
  type: actionTypes.SET_USER_BILLING_INFO,
  userBillingInfo,
});

export interface ISetUserBillingInfoFetching {
  type: actionTypes.SET_USER_BILLING_INFO_FETCHING;
  fetching: boolean;
}

const setUserBillingInfoFetching = (fetching: boolean): ISetUserBillingInfoFetching => ({
  type: actionTypes.SET_USER_BILLING_INFO_FETCHING,
  fetching,
});

export const fetchUserBillingInfo = () => {
  return async (
    dispatch: ThunkDispatch,
    getState: () => StoreState,
  ): Promise<UserBillingInfoDto[]> => {
    const billingState = getState().billing;

    if (billingState.userBillingInfoFetched) {
      return billingState.userBillingInfo;
    }

    const userId = getState().auth.configurableUser!.id;
    dispatch(setUserBillingInfoFetching(true));
    const resp = await BillingInfoResource.getBillingInfo({ userId });
    dispatch(setUserBillingInfo(resp.member));

    return Promise.resolve(resp.member);
  };
};

export const getUserBillingInfoById = (billingInfoId: number) => {
  return async (dispatch: ThunkDispatch, getState: () => StoreState) => {
    const billingState = getState().billing;
    const userId = getState().auth.configurableUser!.id;

    if (billingState.userBillingInfoFetched) {
      return billingState.userBillingInfo.find(bi => bi.id === billingInfoId);
    }

    dispatch(setUserBillingInfoFetching(true));
    const resp = await BillingInfoResource.getBillingInfo({ userId });

    dispatch(setUserBillingInfo(resp.member));

    return Promise.resolve(resp.member.find(bi => bi.id === billingInfoId));
  };
};

// ------------ Subscription plans ------------

export interface ISetFetchingSubscriptionPlans {
  type: actionTypes.SET_FETCHING_SUBSCRIPTION_PLANS;
  fetching: boolean;
}

const setFetchingSubscriptionPlans = (fetching: boolean): ISetFetchingSubscriptionPlans => ({
  type: actionTypes.SET_FETCHING_SUBSCRIPTION_PLANS,
  fetching,
});

export interface ISetSubscriptionPlans {
  type: actionTypes.SET_SUBSCRIPTION_PLANS;
  plans: SalesPackage[];
}

const setSubscriptionPlans = (plans: SalesPackage[]): ISetSubscriptionPlans => ({
  type: actionTypes.SET_SUBSCRIPTION_PLANS,
  plans,
});

export const fetchSubscriptionPlans = () => {
  return async (dispatch: ThunkDispatch, getState: () => StoreState): Promise<SalesPackage[]> => {
    const billingState = getState().billing;

    if (billingState.salesPackages.length > 0) {
      return billingState.salesPackages;
    }

    dispatch(setFetchingSubscriptionPlans(true));
    const plans = await SalesPackageResource.getPlans();
    dispatch(setSubscriptionPlans(plans));
    return Promise.resolve(plans);
  };
};

export const getSubscriptionPlanById = (planId: number) => {
  return (_: Dispatch, getState: () => StoreState): SalesPackage => {
    const { salesPackages } = getState().billing;
    return salesPackages.find(p => p.id === planId)!;
  };
};
// ------------ Country regulations ------------

export interface ISetCountryRegulationsFetching {
  type: actionTypes.SET_COUNTRY_REGULATIONS_FETCHING;
  fetching: boolean;
}

const setCountryRegulationsFetching = (fetching: boolean): ISetCountryRegulationsFetching => ({
  type: actionTypes.SET_COUNTRY_REGULATIONS_FETCHING,
  fetching,
});

export interface ISetCountryRegulations {
  type: actionTypes.SET_COUNTRY_REGULATIONS;
  countryRegulations: CountryRegulation[];
}

const setCountryRegulations = (
  countryRegulations: CountryRegulation[],
): ISetCountryRegulations => ({
  type: actionTypes.SET_COUNTRY_REGULATIONS,
  countryRegulations,
});

export const getCountryRegulations = () => {
  return async (dispatch: Dispatch, getState: () => StoreState) => {
    const billingState = getState().billing;

    if (billingState.countryRegulations.length > 0) {
      return billingState.countryRegulations;
    }

    dispatch(setCountryRegulationsFetching(true));
    const resp = await CountryRegulationsResource.get();
    dispatch(setCountryRegulations(resp.member));

    return Promise.resolve(resp.member);
  };
};

// ------------ Purchase orders ------------

export interface ISetPurchaseOrdersFetching {
  type: actionTypes.SET_PURCHASE_ORDERS_FETCHING;
  fetching: boolean;
}

export const setPurchaseOrdersFetching = (fetching: boolean): ISetPurchaseOrdersFetching => ({
  type: actionTypes.SET_PURCHASE_ORDERS_FETCHING,
  fetching,
});

export interface IAddPurchaseOrder {
  type: actionTypes.ADD_PURCAHSE_ORDER;
  order: Order;
}

export const addPurchaseOrder = (order: Order): IAddPurchaseOrder => ({
  type: actionTypes.ADD_PURCAHSE_ORDER,
  order,
});

export interface ISetPurchaseOrders {
  type: actionTypes.SET_PURCHASE_ORDERS;
  orders: Order[];
}

export const setPurchaseOrders = (orders: Order[]): ISetPurchaseOrders => ({
  type: actionTypes.SET_PURCHASE_ORDERS,
  orders,
});

export interface ICancelPaypalOrder {
  type: actionTypes.CANCEL_PAYPAL_ORDER;
  paypalPaymentId: string;
}

const cancelPaypalOrderData = (paypalPaymentId: string): ICancelPaypalOrder => ({
  type: actionTypes.CANCEL_PAYPAL_ORDER,
  paypalPaymentId,
});

export const cancelPaypalOrder = (paypalPaymentId: string) => {
  return async (dispatch: ThunkDispatch, getState: () => StoreState): Promise<void> => {
    dispatch(cancelPaypalOrderData(paypalPaymentId));
    const billingState = getState().billing;

    if (billingState.userBillingInfo.length === 0) {
      fetchUserBillingInfo()(dispatch, getState);
    }
  };
};

export const fetchOrders = (userId: string) => {
  return async (dispatch: ThunkDispatch) => {
    dispatch(setPurchaseOrdersFetching(true));
    const resp = await OrderResource.getOrders({ userId });
    const orders = resp.member.map(mapOrder);
    dispatch(setPurchaseOrders(orders));
  };
};

// ------------ Billing flow ------------

export interface ISetActiveSalesPackage {
  type: actionTypes.SET_ACTIVE_SALES_PACKAGE;
  selector?: SalesPackageSelector;
}

export const setActiveSalesPackage = (
  selector: SalesPackageSelector | undefined,
): ISetActiveSalesPackage => ({
  type: actionTypes.SET_ACTIVE_SALES_PACKAGE,
  selector,
});

export interface ISetPayerType {
  type: actionTypes.SET_PAYER_TYPE;
  payerType?: Payer;
}

export const setPayerType = (payerType: Payer | undefined): ISetPayerType => ({
  type: actionTypes.SET_PAYER_TYPE,
  payerType,
});

export interface ISetPaymentCountry {
  type: actionTypes.SET_PAYMENT_COUNTRY;
  paymentCountry: string;
}

export const setPaymentCountry = (paymentCountry: string): ISetPaymentCountry => ({
  type: actionTypes.SET_PAYMENT_COUNTRY,
  paymentCountry,
});

export interface ISetPayerVatRegistered {
  type: actionTypes.SET_PAYER_VAT_REGISTERED;
  vatRegistered: boolean;
}

export const setPayerVatRegistered = (vatRegistered: boolean): ISetPayerVatRegistered => ({
  type: actionTypes.SET_PAYER_VAT_REGISTERED,
  vatRegistered,
});

export interface ISetPayerVatNumber {
  type: actionTypes.SET_PAYER_VAT_NUMBER;
  vatNumber: string;
}

export const setPayerVatNumber = (vatNumber: string): ISetPayerVatNumber => ({
  type: actionTypes.SET_PAYER_VAT_NUMBER,
  vatNumber,
});

export interface ISetVatVerificationResponse {
  type: actionTypes.SET_VAT_VERIFICATION_RESPONSE;
  vatVerification: VatVerificationResponse;
}

export const setVatVerificationResponse = (
  vatVerification: VatVerificationResponse,
): ISetVatVerificationResponse => ({
  type: actionTypes.SET_VAT_VERIFICATION_RESPONSE,
  vatVerification,
});

export interface ISetPaymentBillingInfo {
  type: actionTypes.SET_PAYMENT_BILLING_INFO;
  userBillingInfo?: UserBillingInfoDto;
}

export const setPaymentBillingInfo = (
  userBillingInfo: UserBillingInfoDto | undefined,
): ISetPaymentBillingInfo => ({
  type: actionTypes.SET_PAYMENT_BILLING_INFO,
  userBillingInfo,
});

export interface ISetVatCalculations {
  type: actionTypes.SET_VAT_CALCULATIONS;
  vatCalculations: VatCalculateResponse;
}

export const setVatCalculations = (vatCalculations: VatCalculateResponse): ISetVatCalculations => ({
  type: actionTypes.SET_VAT_CALCULATIONS,
  vatCalculations,
});

export interface ISetPurchasingManagementIssuedOrder {
  type: actionTypes.SET_PURCHASING_MANAGEMENT_ISSUED_ORDER;
  purchasingManagementIssuedOrder: boolean;
}

export const setPurchasingManagementIssuedOrder = (
  purchasingManagementIssuedOrder: boolean,
): ISetPurchasingManagementIssuedOrder => ({
  type: actionTypes.SET_PURCHASING_MANAGEMENT_ISSUED_ORDER,
  purchasingManagementIssuedOrder,
});
